/**
 * 设备日志消息翻译映射表
 * 将 FarmBot OS 生成的所有日志消息翻译成中文
 * 包含从 FarmBot OS 源码提取的所有日志消息
 */

export interface LogTranslationMap {
  [key: string]: string;
}

/**
 * 日志翻译相关的本地存储键名
 */
const LOG_TRANSLATION_STORAGE_KEY = "enable_log_translation";

/**
 * 获取日志翻译开关设置
 * @returns 是否启用日志翻译（默认启用）
 */
export function getLogTranslationSetting(): boolean {
  try {
    const stored = localStorage.getItem(LOG_TRANSLATION_STORAGE_KEY);
    // 如果没有存储过，默认返回 true（启用翻译）
    if (stored === null) {
      return true;
    }
    return stored === "true";
  } catch (error) {
    console.error("Failed to read log translation setting:", error);
    return true; // 错误时也默认启用
  }
}

/**
 * 设置日志翻译开关
 * @param enabled - 是否启用翻译
 */
export function setLogTranslationSetting(enabled: boolean): void {
  try {
    localStorage.setItem(LOG_TRANSLATION_STORAGE_KEY, String(enabled));
  } catch (error) {
    console.error("Failed to save log translation setting:", error);
  }
}

/**
 * FarmBot OS 日志消息的中文翻译
 * 从 FarmBot OS 源码中提取的所有日志消息
 */
export const DEVICE_LOG_TRANSLATIONS: LogTranslationMap = {
  // ==================== 系统启动和状态 ====================
  "FarmBot is booted": "FarmBot 已启动",
  "FarmBot is booted.": "FarmBot 已启动",
  "Farmduino OK": "Farmduino 正常",
  "System ready": "系统就绪",
  "System starting": "系统正在启动",
  "Rebooting system": "正在重启系统",
  "System reboot complete": "系统重启完成",
  "Shutdown initiated": "已启动关机",
  "Starting app: :farmbot": "正在启动 FarmBot 应用",
  "Stopping app: :farmbot": "正在停止 FarmBot 应用",
  "Rebooting after {{ uptime }} days of uptime.": "已运行 {{ uptime }} 天，正在重启",

  // ==================== 移动相关 ====================
  "Movement complete": "移动完成",
  "Movement aborted": "移动已中止",
  "Movement failed": "移动失败",
  "Movement failed.": "移动失败。",
  "Movement failed. \"Emergency locked\"": "移动失败。\"紧急锁定\"",
  "Movement cancelled": "移动已取消",
  "Motor stalled": "电机卡死",
  "Moving to safe Z": "移动到安全 Z 轴位置",
  "Retrying movement": "正在重试移动",
  "Homing X axis": "X 轴回零中",
  "Homing Y axis": "Y 轴回零中",
  "Homing Z axis": "Z 轴回零中",
  "Finding home on x": "正在查找 X 轴原点",
  "Finding home on y": "正在查找 Y 轴原点",
  "Finding home on z": "正在查找 Z 轴原点",
  "Finding X axis length": "正在查找 X 轴长度",
  "Finding Y axis length": "正在查找 Y 轴长度",
  "Finding Z axis length": "正在查找 Z 轴长度",
  "X axis length: {{ value }}": "X 轴长度: {{ value }}",
  "Y axis length: {{ value }}": "Y 轴长度: {{ value }}",
  "Z axis length: {{ value }}": "Z 轴长度: {{ value }}",
  "Stop at max enabled": "已启用最大位置停止",
  "Stop at home enabled": "已启用原点停止",
  "Stop at home disabled": "已禁用原点停止",

  // ==================== 序列执行 ====================
  "Sequence started": "序列已开始",
  "Sequence complete": "序列执行完成",
  "Sequence aborted": "序列已中止",
  "Sequence failed": "序列执行失败",
  "Running sequence": "正在运行序列",
  "Skipping sequence step": "跳过序列步骤",
  "Sequence {{ name }} started": "序列 {{ name }} 已开始",
  "Sequence {{ name }} complete": "序列 {{ name }} 执行完成",
  "Executing step {{ number }}": "正在执行步骤 {{ number }}",
  "Step {{ number }} failed": "步骤 {{ number }} 执行失败",
  "Skipping step {{ number }}": "跳过步骤 {{ number }}",

  // ==================== 工具和外围设备 ====================
  "Tool mounted": "工具已安装",
  "Tool dismounted": "工具已卸载",
  "Pinning tool {{ tool }}": "正在固定工具 {{ tool }}",
  "Unpinning tool {{ tool }}": "正在释放工具 {{ tool }}",
  "Tool changed to {{ tool }}": "工具已更改为 {{ tool }}",
  "Tool ID not found.": "未找到工具 ID",
  "Watering plant": "正在浇水",
  "Watering complete": "浇水完成",
  "Peripheral {{ name }} ON": "外设 {{ name }} 已开启",
  "Peripheral {{ name }} OFF": "外设 {{ name }} 已关闭",
  "Toggle peripheral {{ name }}": "切换外设 {{ name }}",
  "Peripheral not found": "未找到外设",
  "Tool not found": "未找到工具",
  "Read peripheral: {{ peripheral.label }}": "读取外设: {{ peripheral.label }}",
  "Read peripheral: {{ peripheral.label }} ok": "读取外设: {{ peripheral.label }} 成功",
  "Not reading peripheral. Firmware not idle.": "未读取外设。固件未空闲。",
  "Not reading peripheral. Firmware not started.": "未读取外设。固件未启动。",

  // ==================== 传感器和引脚控制 ====================
  "Reading sensor {{ name }}": "正在读取传感器 {{ name }}",
  "Sensor reading: {{ value }}": "传感器读数: {{ value }}",
  "Sensor calibration started": "传感器校准已开始",
  "Sensor calibration complete": "传感器校准完成",
  "Temperature: {{ value }}": "温度: {{ value }}",
  "Humidity: {{ value }}": "湿度: {{ value }}",
  "Soil moisture: {{ value }}": "土壤湿度: {{ value }}",
  "Soil moisture complete": "土壤湿度测量完成",
  "The {{ label }} sensor value is 0 (digital)": "{{ label }} 传感器值为 0 (数字)",
  "The {{ label }} sensor value is 1 (digital)": "{{ label }} 传感器值为 1 (数字)",
  "Pin {{ pin_number }} is {{ value }} (analog)": "引脚 {{ pin_number }} 值为 {{ value }} (模拟)",
  "Pin {{ pin_number }} value is OFF (digital)": "引脚 {{ pin_number }} 值为 OFF (数字)",
  "Pin {{ pin_number }} value is ON (digital)": "引脚 {{ pin_number }} 值为 ON (数字)",
  "Turning Boxled3 ON": "开启 Boxled3",
  "Turning Boxled3 OFF": "关闭 Boxled3",
  "Turning Boxled4 ON": "开启 Boxled4",
  "Turning Boxled4 OFF": "关闭 Boxled4",

  // ==================== 照片和图像 ====================
  "Taking photo": "正在拍摄照片",
  "Photo taken": "照片已拍摄",
  "Photo failed": "照片拍摄失败",
  "Camera error": "相机错误",
  "Camera not found": "未找到相机",
  "Uploading photo": "正在上传照片",
  "Photo uploaded": "照片已上传",
  "Processing image": "正在处理图像",
  "Image processed": "图像处理完成",
  "Taking photo {{ current }} of {{ total }}": "正在拍摄照片 {{ current }}/{{ total }}",
  "Uploaded image: {{ file }}": "图片已上传: {{ file }}",
  "Upload Error (": "上传错误 (",
  "Failed to upload image:": "图片上传失败:",

  // ==================== 相机校准 ====================
  "[camera-calibration] Camera calibration complete; setting pixel coordinate scale to {{ scale }} and camera rotation to {{ rotation }} degrees.": "[相机校准] 相机校准完成；设置像素坐标比例为 {{ scale }}，相机旋转角度为 {{ rotation }} 度。",
  "[camera-calibration] Taking camera calibration photo. (1/3)": "[相机校准] 正在拍摄相机校准照片。(1/3)",
  "[camera-calibration] Taking camera calibration photo. (2/3)": "[相机校准] 正在拍摄相机校准照片。(2/3)",
  "[camera-calibration] Taking camera calibration photo. (3/3)": "[相机校准] 正在拍摄相机校准照片。(3/3)",
  "[camera-calibration] Moving to next camera calibration photo location.": "[相机校准] 正在移动到下一个相机校准照片位置。",
  "[camera-calibration] Returning to starting location…": "[相机校准] 正在返回起始位置…",
  "[camera-calibration] Returning to starting location...": "[相机校准] 正在返回起始位置…",
  "[camera-calibration] ERROR: Calibration failed, calibration object not detected in image. Check recent photos.": "[相机校准] 错误：校准失败，图像中未检测到校准对象。检查最近的照片。",
  "Camera calibration complete; setting pixel coordinate scale to {{ scale }} and camera rotation to {{ rotation }} degrees.": "相机校准完成；设置像素坐标比例为 {{ scale }}，相机旋转角度为 {{ rotation }} 度。",
  "[camera-calibration] Taking camera calibration photo. ({{ num }}/3)": "[相机校准] 正在拍摄相机校准照片。({{ num }}/3)",

  // ==================== 网络和连接 ====================
  "Connected to WiFi": "已连接到 WiFi",
  "WiFi connection lost": "WiFi 连接丢失",
  "Reconnecting to WiFi": "正在重新连接 WiFi",
  "MQTT connected": "MQTT 已连接",
  "MQTT disconnected": "MQTT 已断开",
  "Reconnecting to MQTT broker": "正在重新连接 MQTT 代理",
  "Network timeout": "网络超时",
  "Connection timeout": "连接超时",
  "API request failed": "API 请求失败",
  "API request successful": "API 请求成功",
  "Ping failed.": "Ping 失败。",
  "Network interface needs configuration: {{ ifname }}": "网络接口 {{ ifname }} 需要配置",
  "Setting up network interface: {{ ifname }}": "正在设置网络接口: {{ ifname }}",
  "Interface {{ ifname }} connected access point": "接口 {{ ifname }} 已连接到接入点",
  "Interface {{ ifname }} connected to internet": "接口 {{ ifname }} 已连接到互联网",
  "Interface {{ ifname }} connected to local area network": "接口 {{ ifname }} 已连接到局域网",
  "Interface {{ ifname }} disconnected from access point": "接口 {{ ifname }} 已断开接入点连接",
  "AP list was empty. Trying again": "接入点列表为空。正在重试",

  // ==================== 固件和系统更新 ====================
  "Firmware update available": "有可用的固件更新",
  "Firmware update started": "固件更新已开始",
  "Firmware update complete": "固件更新完成",
  "Firmware update failed": "固件更新失败",
  "Firmware Message: ": "固件消息: ",
  "Firmware restart initiated": "固件重启已启动",
  "Firmware terminated.": "固件已终止",
  "Writing firmware to MCU...": "正在写入固件到 MCU...",
  "Begin MCU reset": "开始 MCU 重置",
  "Finish MCU Reset": "MCU 重置完成",
  "Flash failed:": "刷写失败:",
  "Unexpected flash failure": "意外的刷写失败",
  "Begin flash attempt {{ attempts }}": "开始第 {{ attempts }} 次刷写尝试",
  "Failed after {{ attempts }} attempts.": "尝试 {{ attempts }} 次后失败。",
  "Attempt {{ attempts }} failed.": "第 {{ attempts }} 次尝试失败。",
  "Arduino Firmware going down for factory reset!": "Arduino 固件即将关闭以进行工厂重置！",
  "Arduino Firmware going down for firmware update!": "Arduino 固件即将关闭以进行固件更新！",
  "Arduino Firmware going down for firmware update from URL: {{ url }}!": "Arduino 固件即将关闭以进行固件更新，来源: {{ url }}！",
  "Detecting available UARTs: {{ uarts }}": "检测到可用的 UART: {{ uarts }}",

  // ==================== 系统信息 ====================
  "FarmBot OS version: {{ version }}": "FarmBot OS 版本: {{ version }}",
  "Firmware version: {{ version }}": "固件版本: {{ version }}",
  "Arduino firmware version: {{ version }}": "Arduino 固件版本: {{ version }}",
  "Raspberry Pi model: {{ model }}": "树莓派型号: {{ model }}",

  // ==================== 紧急停止和错误 ====================
  "Emergency stop activated": "紧急停止已激活",
  "Emergency stop cleared": "紧急停止已清除",
  "Emergency locked": "紧急锁定",
  "Emergency lock failed. Powering down.": "紧急锁定失败。正在关闭电源。",
  "Stopping emergency lock": "正在解除紧急锁定",
  "E-stopped": "紧急停止",
  "Unlocked": "已解锁",
  "Emergency stop button pressed!": "紧急停止按钮已按下！",
  "Motor temperature high": "电机温度过高",
  "Voltage low": "电压过低",
  "Voltage critical": "电压严重不足",
  "Power loss detected": "检测到电源丢失",
  "Connection lost to device": "与设备失去连接",
  "Device not responding": "设备无响应",
  "Command timeout": "命令超时",
  "Invalid command": "无效的命令",
  "Command not supported": "不支持此命令",
  "Parameter out of range": "参数超出范围",
  "Coordinate out of bounds": "坐标超出范围",
  "Position unreachable": "位置不可达",
  "Collision detected": "检测到碰撞",
  "Position estimate error": "位置估算错误",
  "Encoder error": "编码器错误",
  "Limit switch triggered": "限位开关已触发",
  "End stop reached": "已到达终点停止位",
  "Home position not found": "未找到原点位置",
  "UART timeout:": "UART 超时:",
  "Timeout waiting on": "等待",

  // ==================== 配置和设置 ====================
  "Configuration updated": "配置已更新",
  "Settings saved": "设置已保存",
  "Factory reset initiated": "已启动工厂重置",
  "Factory reset complete": "工厂重置完成",
  "Calibration started": "校准已开始",
  "Calibration complete": "校准完成",
  "Calibration failed": "校准失败",
  "Calibration aborted": "校准已中止",
  "EEPROM cleared": "EEPROM 已清除",
  "Parameters reset": "参数已重置",
  "Configuration complete": "配置完成",
  "Configuration success!": "配置成功！",
  "Configuration FAIL": "配置失败",
  "Set {{ param }} to {{ value }}": "将 {{ param }} 设置为 {{ value }}",
  "Set OS auto update to {{ bool }}": "将 OS 自动更新设置为 {{ bool }}",
  "=== Uploading configuration": "=== 正在上传配置",

  // ==================== 养护和浇水 ====================
  "Regimen started": "养护计划已开始",
  "Regimen complete": "养护计划已完成",
  "Regimen paused": "养护计划已暂停",
  "Regimen resumed": "养护计划已恢复",
  "Regimen skipped": "养护计划已跳过",
  "Running regimen": "正在运行养护计划",
  "Regimen {{ name }} started": "养护计划 {{ name }} 已开始",
  "Regimen {{ name }} complete": "养护计划 {{ name }} 已完成",
  "Watering regimen active": "浇水养护计划激活中",
  "Farm event triggered": "农场事件已触发",
  "Scheduled event executed": "计划事件已执行",
  "Event skipped": "事件已跳过",

  // ==================== 同步和数据 ====================
  "Synced": "已同步",
  "Syncing data": "正在同步数据",
  "Data synced": "数据已同步",
  "Auto sync complete": "自动同步完成",
  "Auto sync failed": "自动同步失败",
  "Download complete": "下载完成",
  "Upload complete": "上传完成",
  "Failed to sync: {{ module }} \n {{ message }}": "同步失败: {{ module }} \n {{ message }}",
  "Error preloading.": "预加载错误。",
  "Cached item is out of date": "缓存项已过期",
  "dropping cache for: {{ state.module }}": "正在清除缓存: {{ state.module }}",
  "Updating:": "正在更新:",
  "Not updating: ": "未更新: ",

  // ==================== Farmware ====================
  "Farmware {{ name }} started": "Farmware {{ name }} 已启动",
  "Farmware {{ name }} complete": "Farmware {{ name }} 已完成",
  "Farmware {{ name }} failed": "Farmware {{ name }} 执行失败",
  "Farmware installed": "Farmware 已安装",
  "Farmware uninstalled": "Farmware 已卸载",
  "Farmware update available": "Farmware 有可用更新",
  "Running Farmware {{ name }}": "正在运行 Farmware {{ name }}",
  "Farmware not installed": "Farmware 未安装",
  "Farmware exit": "Farmware 退出",
  "Terminating farmware process": "正在终止 Farmware 进程",
  "executing rpc from farmware:": "正在从 Farmware 执行 rpc:",
  "=== PYTHONPATH: ": "=== PYTHONPATH: ",

  // ==================== 数据存储 ====================
  "Saving data": "正在保存数据",
  "Data saved": "数据已保存",
  "Saving failed": "保存失败",
  "Loading data": "正在加载数据",
  "Data loaded": "数据已加载",
  "Loading failed": "加载失败",
  "Cache cleared": "缓存已清除",
  "Memory cleared": "内存已清除",
  "Arrays can not be serialized to filesystem nodes": "数组无法序列化为文件系统节点",
  "Filesystem not up yet": "文件系统尚未就绪",

  // ==================== 系统状态和健康 ====================
  "FarmBot is ready": "FarmBot 已准备就绪",
  "Ready for commands": "准备好接收命令",
  "Waiting for command": "等待命令",
  "Idle": "空闲中",
  "Busy": "忙碌中",
  "Online": "在线",
  "Offline": "离线",
  "Update available": "有可用更新",
  "Up to date": "已是最新版本",
  "Uptime: {{ uptime }}": "运行时间: {{ uptime }}",
  "Free memory: {{ memory }}": "可用内存: {{ memory }}",
  "CPU usage: {{ usage }}": "CPU 使用率: {{ usage }}",
  "Disk usage: {{ usage }}": "磁盘使用率: {{ usage }}",
  "WiFi signal: {{ signal }}": "WiFi 信号: {{ signal }}",
  "IP address: {{ ip }}": "IP 地址: {{ ip }}",

  // ==================== 时间和 RTC ====================
  "Time sync complete": "时间同步完成",
  "Time sync failed": "时间同步失败",
  "RTC error": "RTC 错误",
  "Date/time updated": "日期/时间已更新",
  "detected RTC": "检测到 RTC",
  "Could not detect RTC.": "无法检测到 RTC。",
  "beginning RTC sync": "开始 RTC 同步",
  "set system time from RTC: {{ dt }}": "从 RTC 设置系统时间: {{ dt }}",
  "Not setting system time from RTC. VL bit is unset": "未从 RTC 设置系统时间。VL 位未设置",
  "Setting NTP servers: [{{ ntp_server_1 }}, {{ ntp_server_2 }}]": "设置 NTP 服务器: [{{ ntp_server_1 }}, {{ ntp_server_2 }}]",
  "Using default NTP servers": "使用默认 NTP 服务器",
  "Failed to configure NTP:": "NTP 配置失败:",

  // ==================== 引脚绑定 ====================
  "{{ pin_binding }} triggered, executing Power Off": "{{ pin_binding }} 触发，正在执行关机",
  "{{ pin_binding }} triggered, executing Reboot": "{{ pin_binding }} 触发，正在执行重启",
  "{{ pin_binding }} triggered, executing Sync": "{{ pin_binding }} 触发，正在执行同步",
  "{{ pin_binding }} triggered, executing Take Photo": "{{ pin_binding }} 触发，正在执行拍照",
  "Unknown PinBinding: {{ pin_binding }}": "未知的引脚绑定: {{ pin_binding }}",
  "Failed to start PinBinding GPIO Handler. Not retrying.": "引脚绑定 GPIO 处理器启动失败。不再重试。",
  "StubBindingHandler init": "StubBindingHandler 初始化",

  // ==================== HTTP 和 API ====================
  "HTTP Error": "HTTP 错误",
  "Failed to execute command:": "命令执行失败:",
  "Error getting target meta data:": "获取目标元数据错误:",

  // ==================== CeleryScript ====================
  "CeleryScript error": "CeleryScript 错误",
  "BAD AST: ": "错误的 AST: ",
  "Got bad ast:": "收到错误的 AST:",
  "Point error: Please notify support": "点错误：请通知支持",
  "Test log ABC": "测试日志 ABC",

  // ==================== 公开序列名称（用于动态翻译） ====================
  "Pick from Seed Bin": "从种子箱中选取",
  "Pick from Seed Trough": "从种子槽中选取",
  "Pick from Seed Tray": "从种子托盘中选取",
  "Dispense Water": "浇水",
  "Grid": "网格",
  "Photo Grid": "照片网格",
  "Soil Height Grid": "土壤高度网格",
  "Weed Detection Grid": "杂草检测网格",
  "Water All": "给所有植物浇水",
  "Dismount Tool": "卸载工具",
  "Mount Tool": "安装工具",

  // ==================== 其他系统消息 ====================
  "Deleting:": "正在删除:",
  "Replacing credentials": "正在替换凭据",
  "Invalid credentials for change ownership": "更改所有权的凭据无效",
  "using secret to auth": "使用密钥进行身份验证",
  "calendar for": "日历",
  " hasn't changed": " 的日历未更改",
  "Scheduler crash:": "调度器崩溃:",
  "FW Config ": "固件配置 ",
  " is nil": " 为空",
  "Can't decode log:": "无法解码日志:",
  "unhandled message in error state:": "错误状态下的未处理消息:",
  "{{ config.name }} setup:": "{{ config.name }} 设置:",
  "no pipe": "无管道",
  "opening pipe:": "正在打开管道:",
  "PipeWorker": "PipeWorker",
  "exit": "退出",
  "stray timeout": "超时错误",
  "server valid:": "服务器有效:",
  "Upgrade halted:": "升级已停止:",
  "Bad header:": "错误的头部:",
  "Bad payload:": "错误的负载:",
  "Language changed": "语言已更改",
  "FarmBot is NOT synced to the web app.": "FarmBot 未与 Web 应用同步。",
  "FarmBot is synced to the web app.": "FarmBot 已与 Web 应用同步。",
  "OK": "正常",
  "Error": "错误",
  "Warning": "警告",
  "Info": "信息",
  "Success": "成功",
  "Fun": "趣味",
  "Debug": "调试",
};

/**
 * 模糊匹配翻译 - 用于处理包含变量的消息
 * 支持 Elixir 的 #{variable} 语法和 Vue 的 {{ variable }} 语法
 */
export function fuzzyMatchTranslation(message: string): string | null {
  for (const [key, translation] of Object.entries(DEVICE_LOG_TRANSLATIONS)) {
    // 如果 key 包含模板变量，尝试模糊匹配
    if (key.includes("{{") || key.includes("#{")) {
      // 将 Elixir 的 #{var} 语法转换为正则表达式
      let keyRegex = key;

      // 匹配 Elixir 的 #{...} 语法
      keyRegex = keyRegex.replace(/#\{[^}]+\}/g, "(.+?)");

      // 匹配 Vue 的 {{...}} 语法
      keyRegex = keyRegex.replace(/\{\{[^}]+\}\}/g, "(.+?)");

      // 将 Elixir 的 inspect(...) 转换为正则表达式
      keyRegex = keyRegex.replace(/inspect\([^)]+\)/g, ".+?");

      const regex = new RegExp(`^${keyRegex}$`, "i");
      const messageMatch = message.match(regex);

      if (messageMatch) {
        // 提取变量名
        const elixirVars = (key.match(/#\{(\w+)\}/g) || []).map(m => m.slice(2, -1));
        const vueVars = (key.match(/\{\{(\s*\w+\s*)\}\}/g) || []).map(m => m.trim().slice(2, -2));

        const allVars = [...elixirVars, ...vueVars];
        const values = messageMatch.slice(1);

        // 替换翻译中的变量
        let result = translation;
        allVars.forEach((name, index) => {
          if (values[index]) {
            // 替换 Elixir 语法
            result = result.replace(new RegExp(`#\\{\\s*${name}\\s*\\}`, "g"), values[index]);
            // 替换 Vue 语法
            result = result.replace(new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, "g"), values[index]);
            // 替换 inspect 语法
            result = result.replace(new RegExp(`inspect\\([^)]+\\)`, "g"), values[index]);
          }
        });

        return result;
      }
    }
  }

  return null;
}

/**
 * 翻译设备日志消息
 * @param message - 原始英文消息
 * @param enableTranslation - 是否启用翻译（默认启用）
 * @returns 翻译后的消息或原始消息
 */
export function translateDeviceLog(
  message: string,
  enableTranslation: boolean = true
): string {
  if (!enableTranslation || !message) {
    return message;
  }

  // 1. 精确匹配
  if (DEVICE_LOG_TRANSLATIONS[message]) {
    return DEVICE_LOG_TRANSLATIONS[message];
  }

  // 2. 动态处理 "Unable to install public sequence: XXX"
  const sequenceMatch = message.match(/^Unable to install public sequence: (.+)$/);
  if (sequenceMatch) {
    const sequenceName = sequenceMatch[1];
    const translatedName = DEVICE_LOG_TRANSLATIONS[sequenceName] || sequenceName;
    return `无法安装公开序列：${translatedName}`;
  }

  // 3. 模糊匹配（处理包含变量的消息）
  const fuzzyResult = fuzzyMatchTranslation(message);
  if (fuzzyResult) {
    return fuzzyResult;
  }

  // 4. 如果都没有匹配，返回原始消息
  return message;
}

/**
 * 批量翻译日志消息
 * @param messages - 日志消息数组
 * @param enableTranslation - 是否启用翻译（默认启用）
 * @returns 翻译后的消息数组
 */
export function translateDeviceLogs(
  messages: string[],
  enableTranslation: boolean = true
): string[] {
  return messages.map(msg => translateDeviceLog(msg, enableTranslation));
}
